import React, { FC, useEffect, useState } from "react"
import { graphql } from "gatsby"

import { ContentfulLpQueryQuery } from "@graphqlTypes"
import useCommonUrlsAndNumbers from "@hooks/useCommonUrlsAndNumbers"
import { isClientSide } from "@utils"

// Context
import ContentfulLpContext from "../../../context/ContentfulLpContext/ContentfulLpContext"

// HS Marketing Shared Components
import { DefaultPrefooter } from "@helpscout/hs-marketing-shared-components"

// Shared Components
import Prefooter from "@components/Prefooter/Prefooter"
import SEO from "@components/SEO/SEO"

// LP Block Components
import AccordionBlock from "@components/lp/AccordionBlock/AccordionBlock"
import BentoBoxBlock from "@components/lp/BentoBoxBlock/BentoBoxBlock"
import BespokeComponent from "@components/lp/BespokeComponent/BespokeComponent"
import ContentBlock from "@components/lp/ContentBlock/ContentBlock"
import ContentPlusMediaBlock from "@components/lp/ContentPlusMediaBlock/ContentPlusMediaBlock"
import CtaBlock from "@components/lp/CtaBlock/CtaBlock"
import FeatureGridBlock from "@components/lp/FeatureGridBlock/FeatureGridBlock"
import InlineCTA from "@components/lp/InlineCTA/InlineCTA"
import LogosBlock from "@components/lp/LogosBlock/LogosBlock"
import LpHero from "@components/lp/LpHero/LpHero"
import MediaBlock from "@components/lp/MediaBlock/MediaBlock"
import ResourcesBlock from "@components/lp/ResourcesBlock/ResourcesBlock"
import StatBlock from "@components/lp/StatBlock/StatBlock"
import TabsBlock from "@components/lp/TabsBlock/TabsBlock"
import TestimonialBlock from "@components/lp/TestimonialBlock/TestimonialBlock"
import TextBlock from "@components/lp/TextBlock/TextBlock"

import { ContentfulLPContentDIV } from "./ContentfulLP.styles"

import HomeStyle from "./customCss/HomeStyle"

const CONTENT_COMPONENTS = {
  ContentfulCta: CtaBlock,
  ContentfulLpAccordionBlock: AccordionBlock,
  ContentfulLpBentoBoxBlock: BentoBoxBlock,
  ContentfulLpBespokeComponent: BespokeComponent,
  ContentfulLpContentBlock: ContentBlock,
  ContentfulLpContentPlusMediaBlock: ContentPlusMediaBlock,
  ContentfulLpFeatureGrid: FeatureGridBlock,
  ContentfulLpInlineCta: InlineCTA,
  ContentfulLpHero: LpHero,
  ContentfulLpLogosBlock: LogosBlock,
  ContentfulLpMediaBlock: MediaBlock,
  ContentfulLpResourcesBlock: ResourcesBlock,
  ContentfulLpStatBlock: StatBlock,
  ContentfulLpTabsBlock: TabsBlock,
  ContentfulLpTestimonialBlock: TestimonialBlock,
  ContentfulLpTextBlock: TextBlock,
  ContentfulPrefooter: Prefooter,
}

export const Head: FC<{ data: ContentfulLpQueryQuery }> = ({ data: { contentfulLandingPage } }) => (
  <SEO
    creator={contentfulLandingPage?.creator || "Help Scout"}
    description={contentfulLandingPage?.seoDescription || ""}
    image={contentfulLandingPage?.seoImage?.imgixImage?.url || ""}
    permalink={contentfulLandingPage?.permalink || "/"}
    robots={
      // Blanket "noindex,follow" robots for any LPs with the `/lp/` prefix in the URL (except for the Kitchen Sink testing page)
      !!contentfulLandingPage?.permalink?.match(/\/lp\/(?!kitchen-sink\/).+/) ? "noindex,follow" :
        // Otherwise use the custom robots setting or default to "index,follow"
        (contentfulLandingPage?.robots || "index,follow")
    }
    title={contentfulLandingPage?.seoTitle || ""}
    type={contentfulLandingPage?.ogType || "website"}
  />
)

const ContentfulLP: FC<{ data: ContentfulLpQueryQuery }> = ({ data: { contentfulLandingPage } }) => {
  if (!contentfulLandingPage) return null

  const { demoUrl, signupPlusUrl } = useCommonUrlsAndNumbers()

  const {
    content,
  } = contentfulLandingPage

  const hasPrefooter = content && content.find(({ __typename, componentId }) =>
    __typename === "ContentfulPrefooter" ||
    (__typename === "ContentfulLpBespokeComponent" && `${componentId}`.includes("Prefooter"))
  )

  const [isAnnualDiscountApplied, setIsAnnualDiscountApplied] = useState<boolean>(true)
  const [isSalesPage, setIsSalesPage] = useState<boolean>(false)

  useEffect(() => {
    if (!isClientSide()) return

    const searchParams = new URLSearchParams(window.location.search.replace("?", ""))
    const _isSalesPage = !!searchParams.get("sales")

    setIsSalesPage(_isSalesPage)
  }, [])

  return (
    <ContentfulLpContext.Provider
      value={{
        isAnnualDiscountApplied,
        isSalesPage,
        setIsAnnualDiscountApplied,
      }}
    >
      {contentfulLandingPage.permalink === "/" && <HomeStyle />}
      <ContentfulLPContentDIV>
        {content?.map((contentBlock) => {
          const Component = CONTENT_COMPONENTS[contentBlock.__typename]
          return Component ? (
            <Component
              {...contentBlock}
              {...{
                key: contentBlock.entryTitle
              }}
            />
          ) : null
        })}
        {!hasPrefooter && <DefaultPrefooter {...{ demoUrl, signupPlusUrl }} />}
      </ContentfulLPContentDIV>
    </ContentfulLpContext.Provider>
  )
}


export const LandingPageFragment = graphql`
  fragment LandingPageFragment on ContentfulLandingPage {
    id
    title
    permalink
    description: seoDescription
    image {
      imgixImage {
        url
        gatsbyImageData(width: 814)
      }
    }
    seoImage {
      imgixImage {
        url
        gatsbyImageData(width: 814)
      }
    }
  }
`

export const pageQuery = graphql`
  query ContentfulLPQuery( $id: String! ) {
    contentfulLandingPage(
      id: { eq: $id }
    ) {
      content {
        __typename
        ... on ContentfulLpHero {
          ...LpHeroFragment
          entryTitle
        }
        ... on ContentfulCta {
          ...CtaFragment
          entryTitle
        }
        ... on ContentfulLpAccordionBlock {
          ...AccordionBlockFragment
          entryTitle
        }
        ... on ContentfulLpBentoBoxBlock {
          ...BentoBoxBlockFragment
          entryTitle
        }
        ... on ContentfulLpBespokeComponent {
          ...BespokeComponentFragment
          entryTitle
        }
        ... on ContentfulLpContentBlock {
          ...ContentBlockFragment
          entryTitle
        }
        ... on ContentfulLpFeatureGrid {
          ...FeatureGridBlockFragment
          entryTitle
        }
        ... on ContentfulLpInlineCta {
          ...InlineCtaFragment
          entryTitle
        }
        ... on ContentfulLpTabsBlock {
          ...TabsBlockFragment
          entryTitle
        }
        ... on ContentfulLpLogosBlock {
          ...LogosBlockFragment
          entryTitle
        }
        ... on ContentfulLpContentPlusMediaBlock {
          ...ContentPlusMediaBlockFragment
          entryTitle
        }
        ... on ContentfulLpMediaBlock {
          ...MediaBlockFragment
          entryTitle
        }
        ... on ContentfulLpResourcesBlock {
          ...ResourcesBlockFragment
          entryTitle
        }
        ... on ContentfulLpStatBlock {
          ...StatBlockFragment
          entryTitle
        }
        ... on ContentfulLpTestimonialBlock {
          ...TestimonialBlockFragment
          entryTitle
        }
        ... on ContentfulLpTextBlock {
          ...TextBlockFragment
          entryTitle
        }
        ... on ContentfulLpBespokeComponent {
          componentId
          entryTitle
        }
        ... on ContentfulPrefooter {
          ...PrefooterFragment
          entryTitle
        }
      }
      creator
      hideSiteUi
      id
      image {
        imgixImage {
          gatsbyImageData
        }
      }
      ogType
      permalink
      robots
      seoDescription
      seoImage {
        imgixImage {
          url
        }
      }
      seoTitle
      title
    }
  }
`

export default ContentfulLP
